import { useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import useWindowDimensions from "app/AppRouter";
import SongDetail from "pages/SongsPage/components/SongDetail";
import { useAppDispatch, useAppSelector } from "shared/hooks";
import { SongLocationState } from "shared/navigation";
import { rootSelectors } from "state/rootReducer";
import { actions } from "state/slices/songsSlice";

/**
 * A song's detail page, at /songs/:gid.
 *
 * This used to be a stack of songs kept inside the Discover page, so Back only
 * knew about songs opened there. A song opened from Listen or Feedback went
 * "back" to Discover's search. As a route, every song view is a history entry,
 * and Back (in the app or the browser) returns to whatever came before it.
 */
export default function SongDetailPage(): JSX.Element {
  const { gid = "" } = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { width } = useWindowDimensions();

  // useOpenSong stores the song in history state. Without it (a pasted link),
  // look through the songs the client has already loaded.
  const carried = (location.state as SongLocationState | null)?.song;
  const hasCarried = carried?.gid === gid;
  const found = useAppSelector((state) =>
    hasCarried ? null : rootSelectors.songs.findSong(state, gid)
  );
  const song = hasCarried ? carried : found;
  const art = useAppSelector((state) =>
    rootSelectors.songs.getSongArt(state, gid)
  );

  useEffect(() => {
    if (song) {
      dispatch(actions.viewSong(song));
    }
  }, [song?.gid]);

  if (!song) {
    // There's no endpoint for a single song, so a link to one the client
    // hasn't loaded has nothing to show.
    return <Navigate to="/songs" replace />;
  }

  return (
    <SongDetail
      // Remount per song so the recommendation fetcher and spinner start fresh.
      key={song.gid}
      song={song}
      artUri={art?.artUri ?? null}
      artistUris={art?.artistUris ?? []}
      album={art?.album ?? null}
      albumUri={art?.albumUri ?? null}
      spotifyUri={art?.spotifyUri ?? null}
      fetchRecommendationsHandler={(s, search, algo) =>
        dispatch(actions.fetchRecommendations(s, search, algo))
      }
      includeScore={width > 450}
    />
  );
}
